import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Row, Col, Card, Button, Tag, Space } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { 
  RobotOutlined, 
  FileTextOutlined, 
  CodeOutlined, 
  TeamOutlined,
  ApiOutlined,
  DashboardOutlined,
  BugOutlined,
  CloudOutlined,
  SecurityScanOutlined,
  RocketOutlined,
  BarChartOutlined,
  SafetyOutlined,
  SendOutlined
} from '@ant-design/icons';
import SEO from '../../components/SEO';
import './FeaturesPage.css';

const { Title, Paragraph } = Typography;

const FeaturesPage: React.FC = () => {
  const { t } = useTranslation('landing');

  const features = [
    {
      icon: <RobotOutlined />,
      title: t('landing.features.ai.title'),
      description: t('landing.features.ai.description')
    },
    {
      icon: <FileTextOutlined />,
      title: t('landing.features.documentation.title'),
      description: t('landing.features.documentation.description')
    },
    {
      icon: <CodeOutlined />,
      title: t('landing.features.automation.title'),
      description: t('landing.features.automation.description')
    },
    {
      icon: <TeamOutlined />,
      title: t('landing.features.collaboration.title'),
      description: t('landing.features.collaboration.description')
    },
    {
      icon: <ApiOutlined />,
      title: t('landing.features.integration.title'),
      description: t('landing.features.integration.description')
    },
    {
      icon: <DashboardOutlined />,
      title: t('landing.features.test_management.title'),
      description: t('landing.features.test_management.description')
    },
    {
      icon: <BugOutlined />,
      title: t('landing.features.auto_testing.title'),
      description: t('landing.features.auto_testing.description')
    },
    {
      icon: <SecurityScanOutlined />,
      title: t('landing.features.security.title'),
      description: t('landing.features.security.description')
    },
    {
      icon: <CloudOutlined />,
      title: t('landing.features.cloud.title'),
      description: t('landing.features.cloud.description')
    }
  ];

  return (
    <>
      <SEO
        title="Возможности QATE | Функции AI-платформы управления тестированием"
        description="Изучите все возможности платформы QATE: AI-автоматизация тестов, управление тест-кейсами, интеграции, коллаборация команд, отчетность и аналитика QA процессов."
        keywords="возможности QATE, функции QA платформы, AI автоматизация тестов, управление тест кейсами, QA интеграции, features, test automation, QA tools"
        canonicalUrl="/features"
        ogTitle="Возможности QATE | Функции AI-платформы управления тестированием"
        ogDescription="Изучите все возможности платформы QATE: AI-автоматизация тестов, управление тест-кейсами, интеграции, коллаборация команд."
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Возможности QATE",
          "description": "Полный обзор функций и возможностей AI-платформы управления тестированием QATE",
          "mainEntity": {
            "@type": "SoftwareApplication",
            "name": "QATE",
            "featureList": [
              "AI-автоматизация тестирования",
              "Управление тест-кейсами",
              "Система отчетности",
              "Интеграция с CI/CD",
              "Командная коллаборация",
              "Аналитика QA процессов"
            ]
          }
        }}
      />
      <div className="features-page">
        <div className="features-container">
          {/* Hero Section */}
          <Row justify="center" align="middle" className="hero-section">
            <Col xs={24} md={16} lg={12} style={{ textAlign: 'center' }}>
              <div className="alpha-tag">
                <Tag color="blue">
                  {t('landing.hero.alpha')}
                </Tag>
              </div>
              <Title level={1} className="hero-title">{t('landing.featuresPage.title')}</Title>
              <Paragraph className="hero-subtitle">
                {t('landing.featuresPage.subtitle')}
              </Paragraph>
              <Paragraph className="hero-description">
                {t('landing.featuresPage.description')}
              </Paragraph>
            </Col>
          </Row>

          {/* Features Grid */}
          <div className="features-grid-container">
            {features.map((feature, index) => (
              <div className="feature-item" key={index}>
                <Card hoverable className="feature-card">
                  <div className="feature-content">
                    <div className="feature-icon">{feature.icon}</div>
                    <Title level={4} className="feature-title">{feature.title}</Title>
                    <Paragraph className="feature-description">{feature.description}</Paragraph>
                  </div>
                </Card>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <Row justify="center" className="features-cta">
            <Col xs={24} md={16} lg={12} style={{ textAlign: 'center' }}>
              <Title level={2}>{t('landing.featuresPage.cta.title')}</Title>
              <Paragraph className="cta-subtitle">
                {t('landing.featuresPage.cta.subtitle')}
              </Paragraph>
              <Link to="/pricing">
                <Button 
                  type="primary" 
                  size="large"
                  className="cta-button"
                  icon={<RocketOutlined />}
                >
                  {t('landing.featuresPage.cta.button')}
                </Button>
              </Link>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default FeaturesPage; 